import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useHelpScout } from 'src/utils/hooks';
import i18n from 'src/utils/translate';
import { addConditions, fetchProfile } from 'src/modules/profileSurvey/actions';
import Icon from 'src/components/icon';
import Modal, { Footer } from 'src/typescript/components/modal';
import { LinkButton } from 'styles-js/buttons';
import { State } from 'src/types';
import { closeModal, showModal } from 'src/modules/shared/modal/actions';
import ModalType from 'src/constants/modalTypes';
import { titleMedium, titleSmall } from 'styles-js/mixins/typography';
import { trackFootfallEvent } from 'src/utils/footfall';
import EventType from 'src/constants/footfallEventTypes';
import Button from 'src/components/buttons/button';
import { useIsFreePost } from 'src/pages/community/singlePost/singlePostPage';
import { fetchCommunityTopTags } from 'src/modules/community/communityPosts/actions';
import { useIsCommunityMember } from 'src/utils/context';
import { getLocalStorageItem, Key, setLocalStorageItem } from 'src/utils/localStorage';

const formatCondition = ({entityId}: {entityId: number}) => ({
  entityId,
  isPrivate: false,
  selectedConditionReason: 'have',
});

type TopCondition = {
  entityId: number;
  slug: string;
  name: string;
  concept: 'CONDITION';
  rank: number;
}

export default function CommunityTopConditionsModal() {
  const dispatch = useDispatch();
  const conditions = useSelector(({modal}: State) => modal.modalProps.topConditions as TopCondition[]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  useEffect(() => {
    trackFootfallEvent(EventType.CLICKED, { clickType: 'modal-rank1-conditions-viewed' });
  }, []);

  const onChange = (slug: string) => {
    if (selectedConditions.includes(slug)) {
      setSelectedConditions(selectedConditions.filter(c => c !== slug));
    } else {
      setSelectedConditions([...selectedConditions, slug]);
    }
  };

  const onAccept = async () => {
    trackFootfallEvent(EventType.CLICKED, { clickType: 'modal-rank1-conditions-added' });
    if (selectedConditions.length > 0) {
      const conditionsToAdd = conditions.filter(({slug}) => selectedConditions.includes(slug)).map(formatCondition);
      await dispatch(addConditions(conditionsToAdd));
    }
    await dispatch(closeModal());
    dispatch(showModal({modalType: ModalType.PROFILE_SURVEY, modalProps: {screenOverride: [
      'Conditions',
      'ConditionDetails',
      'WellbeingInterests',
      'Gender',
      'Country',
      'Ethnicity',
      'DateOfBirth',
      'Bio',
      'Complete',
    ]}}));
  };

  const onClose = () => {
    trackFootfallEvent(EventType.CLICKED, { clickType: 'modal-rank1-conditions-closed' });
  };

  return (
    <Modal onClose={onClose}>
      <PreTitle>{i18n.t('Top conditions added by members')}</PreTitle>
      <Title>{i18n.t('Do you have any of these conditions?')}</Title>
      <FieldLabel>{i18n.t('Please select all that apply:')}</FieldLabel>
      {conditions.map((condition) =>
        <Label key={condition.slug}>
          <input type="checkbox" onChange={() => onChange(condition.slug)} />
          {condition.name}
        </Label>,
      )}
      <Label>
        <input type="checkbox" onChange={() => setIsOtherSelected(!isOtherSelected)} />
        {i18n.t('Other')}
      </Label>
      <Description>{i18n.t('Answer to add to your profile')}</Description>
      <LinkButton onClick={() => useHelpScout('101-your-profile-on-healthunlocked')}>
        {i18n.t('Learn more')}<Icon icon="right-open" />
      </LinkButton>
      <Footer>
        <Button disabled={selectedConditions.length === 0 && !isOtherSelected} type="primary" onClick={onAccept}>
          {i18n.t('Add')}
        </Button>
      </Footer>
    </Modal>
  );
}

const PreTitle = styled.p`
  color: ${({theme}) => theme.colorTextLight};
  margin-bottom: 10px;
`;
const Title = styled.p`
  ${titleMedium}
  margin-bottom: 20px;
`;
const FieldLabel = styled.p`
  ${titleSmall}
  margin-bottom: 10px;
`;
const Label = styled.label`
  display: block;
  input {
    margin-right: 10px;
  }
`;
const Description = styled.p`
  margin-top: 40px;
`;

export const useTopConditionModal = (): void => {
  const dispatch = useDispatch();
  const topTags = useSelector(({pages}: State) => pages.communityPosts?.topTags);
  const topConditions = topTags?.filter(t => t.rank === 1 && t.concept === 'CONDITION');
  const userConditions = useSelector(({modals}: State) => modals?.profileSurvey?.profileData?.conditions);
  const communityId = useSelector(({context}: State) => context.currentCommunity?.communityId);
  const { currentUser } = useSelector(({context}: State) => context);
  const modalType = useSelector(({modal}: State) => modal.modalType);
  const isFreePost = useIsFreePost();
  const [isModalViewed, setIsModalViewed] = useState(true);

  useEffect(() => {
    if (modalType || isModalViewed || isFreePost || !currentUser || !communityId) return;
    Promise.all([
      dispatch(fetchCommunityTopTags(communityId)),
      dispatch(fetchProfile()),
    ]);
  }, [dispatch, communityId, isFreePost, currentUser, isModalViewed, modalType]);

  useEffect(() => {
    if (!communityId) return setIsModalViewed(true);
    const communityIds = JSON.parse(getLocalStorageItem(Key.communityTopConditionsViewed) || '[]');
    setIsModalViewed(communityIds.includes(communityId));
  }, [communityId]);

  const userHasTopCondition = useMemo(() => {
    if (!topConditions || !userConditions || topConditions.length === 0) return true;
    const topSlugs = topConditions.map(({slug}) => slug);
    const userSlugs = userConditions.map(({slug}) => slug);
    return topSlugs.some((top) => userSlugs.includes(top));
  }, [topConditions, userConditions]);

  const isCommunityMember = useIsCommunityMember();

  useEffect(() => {
    if (userHasTopCondition || isModalViewed || !communityId || !isCommunityMember || modalType) return;
    setIsModalViewed(true);
    const communityIds = JSON.parse(getLocalStorageItem(Key.communityTopConditionsViewed) || '[]');
    setLocalStorageItem(Key.communityTopConditionsViewed, JSON.stringify([...communityIds, communityId]));
    dispatch(showModal({modalType: ModalType.COMMUNITY_TOP_CONDITIONS, modalProps: { topConditions }}));
  }, [userHasTopCondition, dispatch, communityId, topConditions, isCommunityMember, isModalViewed, modalType]);
};
